import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveBar } from '@nivo/bar';
import { Fetch } from '../../utils/Utils.js';
import './NivoChartCard.css';

// Same "emphasis" form as BullSuccessRate.js — one ranked comparison, not
// distinct series, so only the top performer gets the accent.
const ACCENT = '#2a78d6';
const DEEMPHASIS = '#c3c2b7';

// Pools outcomes across every lot/cycle that has ever used a given protocol
// template — distinct from "by lot" (one specific run) or "by bull". Answers
// "does this protocol design work better than that one," not "how did this
// batch of animals do."
const ProtocolSuccessRate = () => {
  const { t } = useTranslation();
  const [bars, setBars] = useState(null);
  const [summary, setSummary] = useState('');

  useEffect(() => {
    let results = null;
    let protocols = null;

    const tryBuild = () => {
      if (!results || !protocols) return;

      const protocolMap = {};
      protocols.forEach(p => { protocolMap[p._id] = p.name || '—'; });

      const agg = {};
      results.forEach(res => {
        const protocolId = res.protocol_id;
        if (!protocolId) return;
        const name = protocolMap[protocolId];
        if (!name) return;
        // A dam dying isn't the protocol's fault — excluded from both the
        // success count and the total, not counted as a failure.
        if (res.outcome === 'dam_died') return;

        if (!agg[name]) agg[name] = { total: 0, success: 0 };
        agg[name].total++;
        if (res.outcome === 'calved_alive' || res.outcome === 'calved_stillborn') agg[name].success++;
      });

      const names = Object.keys(agg);
      if (names.length === 0) {
        setSummary(t('chart_summary_no_protocol_data'));
        setBars(null);
        return;
      }

      // Nivo's horizontal bar layout draws the first array item at the
      // BOTTOM of the chart (band scale, not reading order) — sort ascending
      // so the best performer ends up last in the array, and visually on top.
      // Protocol names run long (localized, e.g. "Zoetis Premium Protocol
      // with GnRH (4-Pass)") — the axis label is truncated so it never gets
      // clipped by the card edge; the full name stays reachable via the
      // tooltip (`name`), never lost, just not force-fit into the axis.
      const truncate = (s) => (s.length > 26 ? `${s.slice(0, 24)}…` : s);
      const rows = names
        .map(name => ({
          name,
          label: truncate(name),
          rate: Math.round((agg[name].success / agg[name].total) * 100),
          success: agg[name].success,
          total: agg[name].total,
        }))
        .sort((a, b) => a.rate - b.rate);
      rows.forEach((r, i) => { r.isBest = i === rows.length - 1; });

      const best = rows[rows.length - 1];
      setSummary(t('chart_summary_best_protocol', { name: best.name, percent: best.rate }));
      setBars(rows);
    };

    Fetch('api/reproduction/results', 'get', null, (data) => { results = data || []; tryBuild(); });
    Fetch('api/data/lots.plans', 'get', null, (data) => { protocols = data || []; tryBuild(); });
  }, [t]);

  return (
    <div className="Chart NivoCard">
      <h3>{t('protocol_success_rate_chart')}</h3>
      <p className="nivo-card-subtitle">{summary}</p>

      {!bars ? (
        <p className="nivo-card-empty">{t('chart_summary_no_protocol_data')}</p>
      ) : (
        <div style={{ height: Math.max(bars.length * 44, 120) }}>
          <ResponsiveBar
            data={bars}
            keys={['rate']}
            indexBy="label"
            layout="horizontal"
            margin={{ top: 8, right: 36, bottom: 42, left: 190 }}
            valueScale={{ type: 'linear', min: 0, max: 100 }}
            padding={0.35}
            borderRadius={4}
            colors={(bar) => (bar.data.isBest ? ACCENT : DEEMPHASIS)}
            enableGridY={false}
            gridXValues={5}
            axisBottom={{ legend: t('success_rate_pct'), legendPosition: 'middle', legendOffset: 32, tickValues: 5, format: (v) => `${v}%` }}
            label={(d) => `${d.value}%`}
            labelSkipWidth={28}
            labelTextColor={(bar) => (bar.data.isBest ? '#ffffff' : '#1a1714')}
            motionConfig="gentle"
            theme={{
              axis: { ticks: { text: { fill: '#898781', fontSize: 12 } }, legend: { text: { fill: '#52514e', fontSize: 12.5 } } },
              grid: { line: { stroke: '#e1e0d9', strokeWidth: 1 } },
              labels: { text: { fontWeight: 700, fontSize: 12.5 } },
            }}
            tooltip={({ data }) => (
              <div className="nivo-tooltip">
                <span className="nivo-tooltip-swatch" style={{ background: data.isBest ? ACCENT : DEEMPHASIS }} />
                <strong>{data.name}</strong>: <span className="nivo-tooltip-value">{t('chart_tooltip_success_fraction', { success: data.success, total: data.total })}</span>
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
};

export default ProtocolSuccessRate;
